import { TONE } from "../../components/Compliance/findings.shared";
import type { StepKey, StepStatus } from "../../service/onboarding.service";

/** Customer-facing copy and status presentation for the onboarding portal. */

/** Nudge's brand indigo as a TONE-shaped entry, from the same values as `--indigo-*` in variable.scss. */
export const INDIGO_TONE = {
  fg: "#4338CA", // --indigo-700
  bg: "#EEF2FF", // --indigo-50
  border: "#C7D2FF", // --secondary-blue-bg
  accent: "#6366F1", // --indigo-500 / colorPrimary
};

type StatusMeta = {
  label: string;
  fg: string;
  bg: string;
  border: string;
  dot: string;
};

export const STATUS_META: Record<StepStatus, StatusMeta> = {
  locked: {
    label: "Up next",
    fg: TONE.neutral.fg,
    bg: "#F3F4F6",
    border: TONE.neutral.border,
    dot: TONE.neutral.accent,
  },
  awaiting_customer: {
    label: "Action needed from you",
    fg: TONE.warning.fg,
    bg: TONE.warning.bg,
    border: TONE.warning.border,
    dot: TONE.warning.accent,
  },
  in_progress_nudge: {
    label: "Nudge is working on this",
    fg: INDIGO_TONE.fg,
    bg: INDIGO_TONE.bg,
    border: INDIGO_TONE.border,
    dot: INDIGO_TONE.accent,
  },
  blocked: {
    label: "Needs attention",
    fg: TONE.danger.fg,
    bg: TONE.danger.bg,
    border: TONE.danger.border,
    dot: TONE.danger.accent,
  },
  complete: {
    label: "Complete",
    fg: TONE.success.fg,
    bg: TONE.success.bg,
    border: TONE.success.border,
    dot: TONE.success.accent,
  },
};

/** Owner chips reuse the badge pairings in `styles/global.scss` (`.badge-soap`, `.badge-default`). */
export const OWNER_META = {
  customer: { label: "You", fg: "#312E81", bg: "#EEF2FF" },
  nudge: { label: "Nudge", fg: "#454545", bg: "#F2F4F7" },
};

/** Per-step body copy, written to answer the questions customers actually ask. */
export const STEP_COPY: Record<StepKey, { blurb: string; nothingNeeded?: boolean }> = {
  baa: {
    // The customer nominates a signer here; nothing is executed until Nudge counter-signs.
    blurb:
      "Before we can access your EHR we need a signed BAA on file — a HIPAA requirement that protects both of us. We've already signed our side; tell us who signs for your practice and we'll email it to them straight away.",
  },
  group_admin: {
    blurb:
      "Your administrator gets a Nudge account that manages your practice. They'll use Member Management to invite your clinicians, revoke access when someone leaves, and look after your shared note templates.",
  },
  ehr_login: {
    // High-level only: the form restates the "create a new user" instruction itself.
    blurb:
      "So we can work inside your EHR, we need a dedicated login of our own — not a shared clinician account. We'll give you the details to register it with, then you send us the credentials.",
  },
  workflow: {
    blurb:
      "Tell us how a typical visit runs at your practice so your templates and AI Medical Assistant match how your team already works. About 2 minutes.",
  },
  templates: {
    blurb:
      "We're building your note templates in Nudge from the five you named, matching the formats already in your EHR — so your clinicians' notes come out looking the way your practice already writes them.",
    nothingNeeded: true,
  },
  ai_assistant: {
    blurb:
      "We're configuring Nudge to work directly inside your EHR — transferring completed notes into the right chart and fields, and handling the click-work your team does manually today. We'll test it end to end before we hand it over.",
    nothingNeeded: true,
  },
  go_live: {
    blurb:
      "Time to bring your clinicians in. You can start now — inviting someone from the list below is also what connects them to their own patient schedule, so the earlier they're in, the sooner that works. We'll book one training session with whoever's leading this at your practice — they'll be set up to bring everyone else on.",
  },
};

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

/** Turn an API error into something the customer can act on. */
export const apiErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as { data?: unknown })?.data;
  // A string body is an HTML error page, never a message for the customer.
  if (!data || typeof data !== "object") return fallback;

  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.trim()) return detail;

  // Depth-first for the first human-readable string in a (possibly nested) map.
  const firstString = (value: unknown): string | null => {
    if (typeof value === "string" && value.trim()) return value;
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = firstString(item);
        if (found) return found;
      }
      return null;
    }
    if (value && typeof value === "object") {
      for (const item of Object.values(value)) {
        const found = firstString(item);
        if (found) return found;
      }
    }
    return null;
  };

  return firstString(data) || fallback;
};
